<template>
  <section class="detail">
    <template v-if="asset">
      <!-- ───────────── Hero ───────────── -->
      <div class="hero">
        <img v-if="image" :src="image" :alt="asset.name" class="hero-img" @error="$emit('image-error')" />
        <img v-else class="hero-img" :src="noImageAvailable" alt="No image available" />
        <div class="hero-scrim"></div>

        <div class="hero-overlay">
          <div class="hero-meta"><v-icon size="14" color="#f0b54a">mdi-map-marker-outline</v-icon><span>{{ metaLine }}</span></div>
          <h2 class="hero-title">{{ asset.name }}</h2>
        </div>

        <!-- Approved-findings badge (bottom-right): just the count of approved findings (the Findings
             button already shows what's left to review). Shimmers until the markers load. -->
        <div class="hero-badge" :title="`${approvedCount} approved findings`">
          <span v-if="loading" class="sk sk-badge"></span>
          <template v-else>
            <v-icon size="15" color="#6ee7a0">mdi-check-circle-outline</v-icon>
            <span class="hero-badge-val">{{ num(approvedCount) }}</span>
            <span class="hero-badge-lbl">approved</span>
          </template>
        </div>
      </div>

      <!-- ───────────── Body ───────────── -->
      <div class="detail-grid">
        <div class="col-left">
          <!-- Action buttons -->
          <div class="action-row">
            <v-tooltip :disabled="!open3dDisabled" location="top" open-delay="200">
              <template #activator="{ props: tip }">
                <button v-bind="tip" type="button" class="card action-card open3d-card"
                  :class="{ disabled: open3dDisabled }" :aria-disabled="open3dDisabled"
                  @click="open3dDisabled ? null : $emit('action', 'model')">
                  <span class="action-icon open3d-icon"><v-icon size="22" :color="open3dDisabled ? '#5a6970' : '#071514'">mdi-cube-outline</v-icon></span>
                  <span class="action-head">
                    <span class="action-title">Open 3D model</span>
                    <v-icon size="20" class="action-arrow open3d-arrow">{{ open3dDisabled ? 'mdi-lock-outline' : 'mdi-arrow-right' }}</v-icon>
                  </span>
                </button>
              </template>
              <span>{{ MODEL_GATE_REASON }}</span>
            </v-tooltip>

            <v-tooltip :disabled="!anomalyDisabled" location="top" open-delay="200">
              <template #activator="{ props: tip }">
                <button v-bind="tip" type="button" class="card action-card anomaly-card"
                  :class="{ disabled: anomalyDisabled }" :aria-disabled="anomalyDisabled"
                  @click="anomalyDisabled ? null : $emit('action', 'anomaly')">
                  <span v-if="pendingCount > 0" class="action-badge" :title="`${pendingCount} pending findings`">{{ pendingCount > 99 ? '99+' : pendingCount }}</span>
                  <span class="action-icon anomaly-icon"><v-icon size="22" :color="anomalyDisabled ? '#5a6970' : '#f0b54a'">mdi-clipboard-pulse-outline</v-icon></span>
                  <span class="action-head">
                    <span class="action-title">Manage Findings</span>
                    <v-icon size="20" class="action-arrow anomaly-arrow">{{ anomalyDisabled ? 'mdi-lock-outline' : 'mdi-arrow-right' }}</v-icon>
                  </span>
                </button>
              </template>
              <span>{{ anomalyReason }}</span>
            </v-tooltip>

            <v-tooltip :disabled="!adminDisabled" location="top" open-delay="200">
              <template #activator="{ props: tip }">
                <button v-bind="tip" type="button" class="card action-card admin-card"
                  :class="{ disabled: adminDisabled }" :aria-disabled="adminDisabled"
                  @click="adminDisabled ? null : $emit('action', 'admin')">
                  <span class="action-icon admin-icon"><v-icon size="22" :color="adminDisabled ? '#5a6970' : '#c792ea'">mdi-cog-outline</v-icon></span>
                  <span class="action-head">
                    <span class="action-title">Asset Admin</span>
                    <v-icon size="20" class="action-arrow admin-arrow">{{ adminDisabled ? 'mdi-lock-outline' : 'mdi-arrow-right' }}</v-icon>
                  </span>
                </button>
              </template>
              <span>You don't have permission to open Asset Admin.</span>
            </v-tooltip>
          </div>

          <v-tooltip v-if="can(Permissions.AnomalyView)" :disabled="!reportDisabled" location="top" open-delay="200">
            <template #activator="{ props: tip }">
              <button v-bind="tip" type="button" class="report-cta" :class="{ disabled: reportDisabled }"
                :aria-disabled="reportDisabled" @click="reportDisabled ? null : $emit('action', 'report')">
                <v-icon size="17">{{ reportDisabled ? 'mdi-lock-outline' : 'mdi-file-chart-outline' }}</v-icon>
                <span>Generate report</span>
              </button>
            </template>
            <span>{{ reportReason }}</span>
          </v-tooltip>

          <!-- Dashboard blocks (2×2 grid, shown only when a current model version exists) -->
          <div v-if="version" class="dash-grid">
            <PieChartComponent :key="`pie-${asset.id}`" class="dash-block" title="Markers by type"
              :data-fetcher="() => AssetService.getAcceptedAnomalyMarkerCountByType(asset.id)" />
            <HotSpotSummaryCard :key="`hot-${asset.id}`" class="dash-block" title="Hotspots"
              :data-fetcher="() => AssetService.getHotSpotMarkersDashboardAsync(asset.id)" />
            <DashboardListComponent :key="`cmt-${asset.id}`" class="dash-block" title="Latest comments"
              :data-fetcher="() => DashboardDataService.getNewestCommentsByAssetId(asset.id)" />
            <InspectionZoneBreakdownCard :key="`zone-${asset.id}`" class="dash-block" title="Inspection zones"
              :data-fetcher="() => InspectionAreaService.getInspectionAreaMarkersAsync(asset.id)" />
          </div>
        </div>

        <!-- Plot plan -->
        <div class="col-right">
          <div class="card plot-card">
            <header class="card-head">
              <span class="card-title">Plot plan</span>
              <span v-if="loading" class="sk sk-pin"></span>
              <span v-else class="pin-count">{{ minimap.points.length }} pins</span>
            </header>
            <div class="plot-stage">
              <div v-if="loading" class="sk sk-map"></div>
              <AnomalyMinimap v-else title="" axis-label="" :points="minimap.points" :fill="minimap.fill" :paths="minimap.paths" @hover="onMarkerHover" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="detail-empty">
      <v-icon size="56" color="#4a5a60">mdi-gesture-tap</v-icon>
      <p class="detail-empty-title">No asset selected</p>
      <p class="detail-empty-sub">Choose an asset from the list on the left to see its preview, stats, and actions.</p>
    </div>
  </section>
</template>

<script setup>
import { defineProps, defineEmits, defineOptions, ref, computed, watch } from 'vue';
import * as THREE from 'three';
import noImageAvailable from '@/assets/no-image-available.svg';
import AnomalyMinimap from '@/components/AnomalyMinimap.vue';
import PieChartComponent from '@/components/charts/PieChartComponent.vue';
import DashboardListComponent from '@/components/cards/DashboardListComponent.vue';
import InspectionZoneBreakdownCard from '@/components/cards/dashboard/InspectionZoneBreakdownCard.vue';
import HotSpotSummaryCard from '@/components/cards/dashboard/HotSpotSummaryCard.vue';
import AssetService from '@/services/AssetService';
import ModelVersionService from '@/services/ModelVersionService';
import InspectionAreaService from '@/services/InspectionAreaService';
import DashboardDataService from '@/services/DashboardDataService';
import organizationSasService from '@/services/OrganizationSasService';
import { dedupeCubemapHelpers, helperPositions, CAMERA_PATH_COLORS } from '@/lib/viewer/pointCloudCull';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

defineOptions({ name: 'AssetDetailPanel' });

const props = defineProps({
  asset: { type: Object, default: null },
  summary: { type: [Object, Boolean], default: null },
  image: { type: String, default: null },
  actionsDisabled: { type: Boolean, default: false },
});

defineEmits(['action', 'image-error']);

const { can } = usePermissions();

const loading = ref(false);
const version = ref(null);
const currentScans = ref([]);
const minimap = ref({ points: [], fill: [], paths: [] });

// Approved-findings badge (hero, bottom-right). Counted from the markers already fetched in
// loadDetailData; rejected/pending are not shown here (the Findings button surfaces what's left).
const approvedCount = ref(0);

let loadToken = 0;   // guards against a stale async load applying after a re-select

const metaLine = computed(() => {
  const a = props.asset;
  if (!a) return '';
  const parts = [];
  if (a.siteTitle) parts.push(a.siteTitle);
  const s = props.summary;
  if (s && typeof s === 'object') {
    if (s.modelVersionNumber != null) parts.push(`V${s.modelVersionNumber}`);
    else if (s.modelVersionName) parts.push(s.modelVersionName);
    if (s.scanCount != null) parts.push(`${s.scanCount} ${s.scanCount === 1 ? 'scan' : 'scans'}`);
  }
  return parts.join(' · ') || 'No scans yet';
});

// Pending findings count → badge on the Anomaly Management action card.
const pendingCount = computed(() => {
  const s = props.summary;
  return s && typeof s === 'object' ? Number(s.totalAnomaliesPending || 0) : 0;
});

// ── Action gating — mirrors the old asset landing page (FunctionalityButton). The model / findings
// / report actions are enabled only once the asset has a current model version whose scans have all
// finished processing; the parent computes that into `actionsDisabled` (Duallo admins bypass). Admin
// is never model-gated — only permission-gated — matching the landing page. Disabled cards show a
// lock affordance + a tooltip explaining why, so the reason is discoverable on hover.
const MODEL_GATE_REASON = 'Model not ready yet — generate or finish processing scans in Asset Admin.';

const open3dDisabled = computed(() => props.actionsDisabled);
const anomalyDisabled = computed(() => props.actionsDisabled || !can(Permissions.AnomalyView));
const adminDisabled = computed(() => !can(Permissions.AssetAdminView));
const reportDisabled = computed(() => props.actionsDisabled || !can(Permissions.AnomalyView));

const anomalyReason = computed(() =>
  can(Permissions.AnomalyView) ? MODEL_GATE_REASON : "You don't have permission to review findings.");
const reportReason = computed(() =>
  can(Permissions.AnomalyView) ? MODEL_GATE_REASON : "You don't have permission to generate reports.");

watch(() => props.asset?.id, (id) => { if (id) loadDetailData(id); else resetData(); }, { immediate: true });

function resetData() {
  version.value = null;
  currentScans.value = [];
  minimap.value = { points: [], fill: [], paths: [] };
  approvedCount.value = 0;
}

async function loadDetailData(assetId) {
  const token = ++loadToken;
  loading.value = true;
  resetData();
  try {
    const [markers, ver, scans] = await Promise.all([
      AssetService.getAnomalyMarkersByAssetId(assetId).catch(() => []),
      ModelVersionService.getCurrentModelVersion(assetId).catch(() => null),
      ModelVersionService.getScansByAssetId(assetId).catch(() => []),
    ]);
    if (token !== loadToken) return;

    // Approved-findings badge: count the approved/accepted markers we just fetched.
    approvedCount.value = (markers || []).filter(m => {
      const s = (m.status || '').toLowerCase();
      return s === 'approved' || s === 'accepted';
    }).length;

    version.value = ver;
    const scanIds = ver ? (ver.scans?.length ? ver.scans.map(s => s.scanId) : [ver.associatedScan]).filter(Boolean) : [];
    currentScans.value = (scans || []).filter(s => (scanIds.length ? scanIds.includes(s.id) : true));

    // ── Plot plan, assembled exactly like AnomalyManagementView: transform every point (markers,
    // footprint, walking paths) into the version world frame, then project top-down (X across, Y depth).
    const matrices = buildScanMatrices(ver);   // scanId -> THREE.Matrix4
    const tmp = new THREE.Vector3();
    const toWorld = (scanId, x, y, z) => { tmp.set(x, y, z); const m = matrices[scanId]; if (m) tmp.applyMatrix4(m); return tmp; };

    // Anomaly pins.
    const points = (markers || [])
      .filter(m => Number.isFinite(m.transform?.position?.x) && Number.isFinite(m.transform?.position?.y) && Number.isFinite(m.transform?.position?.z))
      .map((m, i) => {
        const w = toWorld(m.scanId, m.transform.position.x, m.transform.position.y, m.transform.position.z);
        return {
          id: m.id, u: w.x, v: w.y, number: i + 1,
          color: statusDotColor(m.status), label: m.name || m.label || 'Anomaly',
          hasImage: !!m.thumbnailPath, thumbnailPath: m.thumbnailPath || null, imageSrc: null,
        };
      });

    // Only the current model version's scans feed the plot plan (footprint + walking paths).
    const versionScans = currentScans.value;

    // Footprint: each scan's stored plot slice, transformed + projected + downsampled.
    const fillWorld = [];
    for (const s of versionScans) {
      if (!s?.perimeter) continue;
      let plot;
      try { plot = JSON.parse(s.perimeter)?.plot; } catch { continue; }
      for (const p of (plot?.fill || [])) {
        const w = toWorld(s.id, p[0], p[1], p[2]);
        fillWorld.push([w.x, w.y]);
      }
    }
    const fill = downsamplePairs(fillWorld, 2500);

    // Camera walking path(s): stations ordered by capture sequence, transformed, projected.
    const paths = [];
    for (let idx = 0; idx < versionScans.length; idx++) {
      const s = versionScans[idx];
      try {
        const raw = await AssetService.getCameraHelpersAsync(s.id);
        const ordered = dedupeCubemapHelpers(raw || [])
          .sort((a, b) => (a.imageName ?? '').localeCompare(b.imageName ?? '', undefined, { numeric: true }));
        const pos = helperPositions(ordered);
        if (pos.length < 2) continue;
        const line = pos.map(p => { const w = toWorld(s.id, p.x, p.y, p.z); return [w.x, w.y]; });
        paths.push({ points: line, color: CAMERA_PATH_COLORS[idx % CAMERA_PATH_COLORS.length] });
      } catch { /* skip */ }
    }
    if (token !== loadToken) return;
    minimap.value = { points, fill, paths };
  } finally {
    if (token === loadToken) loading.value = false;
  }
}

// Per-scan world matrix (translate · rotate(euler) · scale) — matches ViewerEngine.applyTransform and
// AnomalyManagementView.scanMatrices. Keyed by scanId; only the current version's scans carry a transform.
function buildScanMatrices(ver) {
  const out = {};
  for (const s of (ver?.scans || [])) {
    if (!s?.scanId) continue;
    const t = s.transform || {};
    const p = t.position || {}, r = t.rotation || {}, sc = t.scale || {};
    const pos = new THREE.Vector3(p.x ?? 0, p.y ?? 0, p.z ?? 0);
    const quat = new THREE.Quaternion().setFromEuler(new THREE.Euler(r.x ?? 0, r.y ?? 0, r.z ?? 0, r.order || 'XYZ'));
    const scl = new THREE.Vector3(sc.x ?? 1, sc.y ?? 1, sc.z ?? 1);
    out[s.scanId] = new THREE.Matrix4().compose(pos, quat, scl);
  }
  return out;
}

function downsamplePairs(arr, cap) {
  if (arr.length <= cap) return arr;
  const stride = arr.length / cap;
  const out = [];
  for (let i = 0; i < cap; i++) out.push(arr[Math.floor(i * stride)]);
  return out;
}

function statusDotColor(status) {
  const s = (status || '').toLowerCase();
  if (s === 'approved' || s === 'accepted') return '#4ade80';
  if (s === 'rejected') return '#f87171';
  return '#f59e0b';
}

function num(value) {
  if (value == null) return '0';
  return Number(value).toLocaleString();
}

// Lazily sign a marker's thumbnail the first time it's hovered on the plot plan, so the
// tooltip can show its image (the minimap shows a shimmer until imageSrc arrives + decodes).
async function onMarkerHover(id) {
  if (!id) return;
  const pt = minimap.value.points.find(p => p.id === id);
  if (!pt || pt.imageSrc || !pt.thumbnailPath || pt._signing) return;
  pt._signing = true;
  try {
    pt.imageSrc = await organizationSasService.getSignedUrl(pt.thumbnailPath);
    if (!pt.imageSrc) pt.hasImage = false;
  } catch {
    pt.hasImage = false;
  }
}
</script>

<style scoped>
.detail {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  padding: 14px;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;   /* backstop: nothing escapes the card horizontally */
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Lay out against the panel's OWN width, so it collapses to fit whatever column it's in
     (viewport-based media queries would be wrong — the panel is narrower than the viewport). */
  container-type: inline-size;
  container-name: adp;
  scrollbar-width: thin;                       /* Firefox */
  scrollbar-color: #3a4a52 transparent;
}

/* Match the asset list's thin custom scrollbar (no default browser/OS chrome). */
.detail::-webkit-scrollbar { width: 6px; }
.detail::-webkit-scrollbar-track { background: transparent; }
.detail::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.detail::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

/* ───────────── Hero ───────────── */
.hero {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  min-height: clamp(180px, calc(60px + 9.4vw), 240px);
  background: #1c272c;
}

.hero-img { width: 100%; height: clamp(180px, calc(60px + 9.4vw), 240px); object-fit: cover; display: block; }

.hero-scrim {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(10, 15, 18, 0.15) 0%, rgba(10, 15, 18, 0) 30%, rgba(10, 15, 18, 0.55) 78%, rgba(10, 15, 18, 0.85) 100%);
}

.hero-overlay { position: absolute; left: 16px; bottom: 14px; right: 16px; }
.hero-meta { display: inline-flex; align-items: center; gap: 5px; font-size: 0.8rem; color: #d7e2e8; margin-bottom: 3px; }
/* Reserve room on the right for the approved badge (bottom-right) and ellipsize a long name so it
   never runs underneath it. */
.hero-title {
  font-size: clamp(1.4rem, calc(9.6px + 1vw), 1.8rem); font-weight: 800; color: #fff; margin: 0;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  padding-right: 128px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* ───────────── Hero approved-progress badge ───────────── */
.hero-badge {
  position: absolute; right: 14px; bottom: 14px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 11px; border-radius: 999px;
  background: rgba(10, 15, 18, 0.62); border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(4px);
  min-height: 30px;
}
.hero-badge-val { font-size: 0.9rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; line-height: 1; }
.hero-badge-lbl { font-size: 0.72rem; font-weight: 600; color: #cfd8dc; letter-spacing: 0.02em; }
.sk-badge { display: block; width: 72px; height: 12px; border-radius: 6px; }

/* ───────────── Body ───────────── */
.detail-grid { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr); gap: 12px; }
.col-left { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.col-right { display: flex; flex-direction: column; gap: 12px; min-width: 0; }

/* Reused dashboard components: flow into as many columns as fit (2 when wide, 1 when
   narrow — resilient to browser/OS zoom), equal-height cells, contents clipped to the cell. */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-auto-rows: clamp(260px, calc(180px + 6.25vw), 300px);
  gap: 12px;
  min-width: 0;
}
.dash-block { min-width: 0; min-height: 0; overflow: hidden; }
.dash-block :deep(.card-body) { min-height: 0; }
/* Bound the ECharts canvas to its cell so the donut can never balloon past the card. */
.dash-block :deep(.chart-canvas) { min-height: 0 !important; height: 100%; }
.dash-block :deep(.zone-card),
.dash-block :deep(.list-card),
.dash-block :deep(.pie-card),
.dash-block :deep(.hotspot-card) { height: 100%; max-height: 100%; }

.card {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: clamp(12px, calc(4px + 0.625vw), 16px);
  min-width: 0;   /* allow the card to shrink inside its grid track instead of overflowing */
}

.card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.card-title { font-size: 1rem; font-weight: 700; color: #fff; }

/* Action buttons (Open 3D model / Dashboard / Admin) */
.action-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }

.action-card {
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 12px;
  min-width: 0; text-align: left; font: inherit; cursor: pointer;
  padding: 16px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
/* Pending-findings badge, inside the top-right corner of the Anomaly Management card. */
.action-badge {
  position: absolute; top: 10px; right: 10px;
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: #f0a623; color: #241700;
  font-size: 0.72rem; font-weight: 800; line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}
.action-card:not(.disabled):hover { border-color: rgba(122,162,255,0.4); background: rgba(122,162,255,0.05); }
/* Disabled: greyed, no pointer, and the icon tile / arrow lose their accent (mirrors the old landing page). */
.action-card.disabled { opacity: 0.5; cursor: not-allowed; }
.action-card.disabled .action-icon { background: rgba(255, 255, 255, 0.05); }
.action-card.disabled .action-arrow { color: #5a6970; }
.action-icon { width: 38px; height: 38px; border-radius: 10px; background: rgba(122,162,255,0.12); display: flex; align-items: center; justify-content: center; }
.action-head { display: flex; align-items: center; gap: 8px; width: 100%; }
.action-title { font-size: 1.05rem; font-weight: 700; color: #fff; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.action-arrow { margin-left: auto; color: #7aa2ff; transition: transform 0.15s ease; flex: 0 0 auto; }
.action-card:not(.disabled):hover .action-arrow { transform: translateX(3px); }

/* Primary CTA — the card's core action, a filled accent button so it clearly leads (mirrors the
   mobile asset-landing al-primary). The other two cards stay as subtle secondary actions. */
.action-card.open3d-card:not(.disabled) {
  background: var(--duallo-accent-gradient);
  border-color: transparent;
}
.action-card.open3d-card:not(.disabled):hover {
  background: var(--duallo-accent-gradient);
  border-color: transparent;
  filter: brightness(1.05);
}
.open3d-card:not(.disabled) .action-title,
.open3d-card:not(.disabled) .open3d-arrow { color: #071514; }
.open3d-card:not(.disabled) .open3d-icon { background: rgba(7, 21, 20, 0.15); }

.admin-card:not(.disabled):hover { border-color: rgba(199, 146, 234, 0.4); background: rgba(199, 146, 234, 0.05); }
.admin-icon { background: rgba(199, 146, 234, 0.12); }
.admin-arrow { color: #c792ea; }

.anomaly-card:not(.disabled):hover { border-color: rgba(240, 181, 74, 0.4); background: rgba(240, 181, 74, 0.05); }
.anomaly-icon { background: rgba(240, 181, 74, 0.12); }
.anomaly-arrow { color: #f0b54a; }

/* Generate report — a secondary, full-width action under the primary cards. */
.report-cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 11px; border-radius: 12px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc; font: inherit; font-size: 0.9rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.report-cta:not(.disabled):hover { background: rgba(102, 199, 180, 0.08); border-color: rgba(102, 199, 180, 0.4); color: #fff; }
.report-cta.disabled { opacity: 0.5; cursor: not-allowed; }

/* Plot plan */
.plot-card { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.pin-count { font-size: 0.8rem; color: #8aa0aa; }
.plot-stage {
  min-width: 0;
  /* overflow visible so a marker's hover image can pop out above the tile (SVG content is
     clipped to its own viewport anyway; HTML pins stay within ~2% of the box). */
  overflow: visible;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.22);   /* matches the minimap's own bg so they read as one surface */
}
/* Flatten the embedded AnomalyMinimap so its background meets the plot-stage edges (no inner card). */
.plot-stage :deep(.minimap) {
  background: none;
  border: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  overflow: visible;
}
.plot-stage :deep(.minimap-head) { display: none; }

/* ───────────── Skeleton shimmer (YouTube-style) ───────────── */
.sk {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}
.sk::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
@keyframes sk-shimmer {
  100% { transform: translateX(100%); }
}
@media (prefers-reduced-motion: reduce) {
  .sk::after { animation: none; }
}

/* Plot plan skeleton */
.sk-pin { display: inline-block; width: 46px; height: 0.8rem; }
.sk-map { width: 100%; height: 100%; min-height: 280px; border-radius: 10px; }

/* Empty */
.detail-empty {
  flex: 1 1 auto; min-height: 320px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; text-align: center; padding: 40px 24px; color: #8aa0aa;
}
.detail-empty-title { margin: 6px 0 0; font-size: 1.05rem; font-weight: 700; color: #cfd8dc; }
.detail-empty-sub { margin: 0; font-size: 0.9rem; max-width: 320px; line-height: 1.5; }

/* Collapse based on the panel's own width (container query), not the viewport. */
@container adp (max-width: 760px) {
  .detail-grid { grid-template-columns: 1fr; }
}

/* Note: no <600px rule — this panel only mounts at ≥960 (below that HomePage navigates to the asset
   landing page instead of rendering the panel), so a phone-width rule would never match. */
</style>
